import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { NOTIFICATIONS_PATH } from './paths';
import type { AppNotification } from './notifications.api';
import { listKey } from './notifications.api';
import { bellGlyph, countLabel } from './notificationKinds';
import { NoticeRow } from './NoticeRow';
import {
  POPOVER_LIMIT,
  useInboxEnabled,
  useNotificationActions,
  useRecentNotifications,
  useUnreadCount,
} from './useNotifications';
import { useNow } from './useNow';
import './notifications.css';

/**
 * The bell in the top bar: the signed-in person's unread count (a cobalt
 * count, the live mark; absent at zero) and a popover with the latest
 * notices. Every tenant, every staff user; agents have no staff inbox.
 *
 * Requests: the count once a minute while the tab is in view (one shared
 * React Query entry, however many bells are mounted); the list only when the
 * popover opens. Clicking a notice marks it read and follows its link.
 */
export function NotificationBell({ className = '' }: { className?: string }) {
  const enabled = useInboxEnabled();
  const unread = useUnreadCount(enabled);
  const { pathname } = useLocation();
  // Open ON a page: a navigation (a notice followed, a link elsewhere) closes it by itself.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const open = openOn === pathname;
  const setOpen = useCallback(
    (v: boolean | ((was: boolean) => boolean)) =>
      setOpenOn((cur) => ((typeof v === 'function' ? v(cur === pathname) : v) ? pathname : null)),
    [pathname],
  );
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const qc = useQueryClient();

  // The count went up: the popover's cached list is old news.
  const lastUnread = useRef(unread);
  useEffect(() => {
    if (unread > lastUnread.current) void qc.invalidateQueries({ queryKey: listKey(POPOVER_LIMIT) });
    lastUnread.current = unread;
  }, [unread, qc]);

  const close = useCallback(
    (refocus: boolean) => {
      setOpen(false);
      if (refocus) btnRef.current?.focus();
    },
    [setOpen],
  );

  // Outside click and Esc, bound while open only.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close, setOpen]);

  if (!enabled) return null;

  const label = unread > 0 ? `Notifications, ${unread > 99 ? 'more than 99' : unread} unread` : 'Notifications';

  return (
    <div ref={wrapRef} className={`nbell ${className}`.trim()} data-tour="bell">
      <button
        ref={btnRef}
        type="button"
        className="nbell__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={label}
      >
        {bellGlyph(18)}
        {unread > 0 && (
          <span className="nbell__count" aria-hidden>
            {countLabel(unread)}
          </span>
        )}
      </button>
      {open && <BellPanel unread={unread} onDone={() => close(false)} />}
    </div>
  );
}

function BellPanel({ unread, onDone }: { unread: number; onDone: () => void }) {
  const { data, isLoading, isError, refetch } = useRecentNotifications(true);
  const { markRead, markAllRead } = useNotificationActions();
  const now = useNow();
  const rows = data?.data ?? [];

  const onOpen = (n: AppNotification) => {
    void markRead(n);
    onDone();
  };

  return (
    <div className="nbell__pop hub-overlay motion-scale" role="dialog" aria-label="Notifications">
      <div className="nbell__head">
        <span className="nbell__kicker">
          Notifications
          {unread > 0 && <span className="nbell__kicker-n">{countLabel(unread)} new</span>}
        </span>
        {unread > 0 && (
          <button type="button" className="nbell__link" onClick={() => void markAllRead()}>
            Mark all as read
          </button>
        )}
      </div>
      <div className="nbell__scroll">
        {isLoading && !data ? (
          <p className="nbell__state">
            <span className="nbell__spinner" aria-hidden /> Loading
          </p>
        ) : isError && !data ? (
          <p className="nbell__state">
            Could not load your notifications.{' '}
            <button type="button" className="nbell__link" onClick={() => void refetch()}>
              Try again
            </button>
          </p>
        ) : rows.length === 0 ? (
          <div className="nbell__empty">
            <span className="nbell__empty-glyph" aria-hidden>
              {bellGlyph(22)}
            </span>
            <p className="nbell__empty-title">You are all caught up</p>
            <p className="nbell__empty-hint">Notices from your modules and the Employee Hub land here.</p>
          </div>
        ) : (
          <ul className="nbell__list">
            {rows.map((n) => (
              <li key={n.id}>
                <NoticeRow n={n} now={now} onOpen={onOpen} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="nbell__foot">
        <Link to={NOTIFICATIONS_PATH} className="nbell__see" onClick={onDone}>
          See all notifications
        </Link>
      </div>
    </div>
  );
}
